import * as amplitude from '@amplitude/analytics-browser';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Box, ThemeProvider } from '@mui/material';
import ChangeAnythingFeedModal from 'apps/studio/studio-tab/components/change-anything-feed-modal/ChangeAnythingFeedModal';
import FeedPromptsModal from 'apps/studio/studio-tab/components/feed-prompts-modal/FeedPromptsModal';
import SubmitPromptForReviewPopUp from 'apps/studio/studio-tab/components/feed-prompts-modal/submit-prompt-for-review-pop-up/SubmitPromptForReviewPopUp';
import axios from 'axios';
import { ReactComponent as UpdateIcon } from 'core/assets/svg/common/app_update.svg';
import 'App.css';
import FloatingAlert from 'core/components/floating-alert/FloatingAlert';
import { APIConfigData } from 'core/config/APIConfigData';
import { LogoutEnum } from 'core/constants/enums/LogoutEnum.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import useCustomNavigate from 'core/hooks/useCustomNavigate';
import LazyAudioCroppingModal from 'core/layouts/components/audio-cropping-modal/LazyAudioCroppingModal';
import DeletePromptConfirmationModal from 'core/layouts/components/delete-prompt-confirmation-modal/DeletePromptConfirmationModal';
import ExpandImageModal from 'core/layouts/components/expand-image-modal/ExpandImageModal';
import HomePromptsModal from 'core/layouts/components/home-prompts-modal/HomePromptsModal';
import MarketingPopUp from 'core/layouts/components/marketing/marketing-pop-up/MarketingPopUp';
import LazyMusicVideoCreateSceneModal from 'core/layouts/components/music-video-create-scene-modal/LazyMusicVideoCreateSceneModal';
import PricingModal from 'core/layouts/components/pricing-modal/PricingModal';
import SignedOutModal from 'core/layouts/components/signed-out-modal/SignedOutModal';
import appTheme from 'core/layouts/ui-blocks/AppTheme';
import 'react-toastify/dist/ReactToastify.css';
import 'core/layouts/components/toast/ToastStyles.css';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import LoadingScreen from 'core/navigation/loading-screen/LoadingScreen';
import { LOGIN } from 'core/navigation/Routes';
import {
	blockUser,
	isAppUpdateAvailableSelector,
	toggleSignedOutModal,
} from 'core/store/reducers/appReducer/AppSlice';
import { googleSignOut } from 'core/store/reducers/appReducer/appSliceThunk';
import {
	openPromptsModalSelector,
	openSelectCanvasModalSelector,
} from 'core/store/reducers/homeReducer/HomeSlice';
import { openSubscriptionModalSelector } from 'core/store/reducers/profileReducer/ProfileSlice';
import { captureDatadogErrors } from 'core/utils/datadogUtil';
import { getFirebaseTokenWithRetries, getStoredFirebaseToken } from 'core/utils/firebaseUtil';
import LazyClothChangeModal from 'platform/web/home-page/components/do-more-section/components/cloth-change-modal/LazyClothChangeModal';
import LazyFaceReplaceModal from 'platform/web/home-page/components/do-more-section/components/face-replace-modal/LazyFaceReplaceModal';
import LazyHomeNarratorModal from 'platform/web/home-page/components/do-more-section/components/narrator-modal/LazyHomeNarratorModal';
import LazyProductPhotoModal from 'platform/web/home-page/components/do-more-section/components/product-photo-modal/LazyProductPhotoModal';
import LazyTrueTouchModal from 'platform/web/home-page/components/do-more-section/components/true-touch-modal/LazyTrueTouchModal';
import LazyUpscale8kModal from 'platform/web/home-page/components/do-more-section/components/upscale-8k-modal/LazyUpscale8kModal';
import LazyCreateNewCharacterModal from 'platform/web/my-characters/create-new-character-modal/LazyCreateNewCharacterModal';
import posthog from 'posthog-js';
import React, { lazy, Suspense, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
// Note: For benchmarking position finder algorithm
// import 'core/utils/tests/canvasPositionFinder.test';

const AssetLibraryModal = lazy(
	() => import('core/layouts/components/asset-library/components/AssetLibraryModal'),
);

const SelectCanvasModal = lazy(
	() => import('platform/web/my-characters/select-canvas-modal/SelectCanvasModal'),
);

const DeletedUserModal = lazy(
	() => import('core/layouts/components/auth-page/components/SignInDeletedAccountModal'),
);
const PoseControlTemplateModal = lazy(
	() =>
		import(
			'core/layouts/components/pose-control-templates/pose-control-templates-modal/PoseControlTemplateModal'
		),
);

const ExpandVideoModal = lazy(
	() => import('core/layouts/components/expand-video-modal/ExpandVideoModal'),
);
const FluxCharacterSelectModal = lazy(
	() =>
		import(
			'platform/web/home-page/components/flux-character-select-modal/FluxCharacterSelectModal'
		),
);
const VideoAnyoneModal = lazy(
	() => import('platform/web/home-page/components/video-anyone-modal/VideoAnyoneModal'),
);

function App() {
	// clearLocalStorage();
	const dispatch = useDispatch();
	const navigate = useCustomNavigate();

	const openHomePromptsModal = useSelector(openPromptsModalSelector);
	const openPricingModal = useSelector(openSubscriptionModalSelector);
	const openSelectCanvasModal = useSelector(openSelectCanvasModalSelector);
	const isAppUpdateAvailable = useSelector(isAppUpdateAvailableSelector);

	const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;
	// useEffect(() => {
	// 	dataApiHandler({
	// 		endpoint: canvasListEndpoints.endpoints.listCanvasData.initiate,
	// 		args: { page: null, pageSize: null },
	// 		dispatch,
	// 	});
	// }, [dispatch]);

	useEffect(() => {
		axios.interceptors.request.use(
			(config) => {
				const c = config;

				return getStoredFirebaseToken()
					.then((token) => {
						try {
							c.headers.Authorization = `Bearer ${token}`;
							c.headers.Provider = APIConfigData.PROVIDER_HEADER;
						} catch (error) {
							captureDatadogErrors('Axios headers catch', {}, error);
						}

						return c;
					})
					.catch((error) => {
						captureDatadogErrors('Firebase Token catch', {}, error);
					});
			},
			(error) => {
				const { config } = error;
				if (config.retryDone) {
					Promise.reject(error);
				} else {
					getFirebaseTokenWithRetries()
						.then((token) => {
							config.retryDone = true;
							config.headers.Authorization = `Bearer ${token}`;
							config.headers.Provider = APIConfigData.PROVIDER_HEADER;
							return axios(config);
						})
						.catch((tokenError) => {
							Promise.reject(tokenError);
						});
				}
			},
		);
		axios.interceptors.response.use(undefined, (error) => {
			if (
				error?.response?.data?.err.code === RendernetErrors.INVALID_CREDENTIALS ||
				error?.response?.data?.err?.message === 'No session found'
			) {
				dispatch(googleSignOut(LogoutEnum.RESPONSE_INTERCEPTOR));
				navigate(`/${LOGIN}`);
				dispatch(toggleSignedOutModal(true));
			}
			if (error?.response?.data?.err.code === RendernetErrors.BLOCKED_USER) {
				dispatch(blockUser());
			}
			return Promise.reject(error);
		});
	}, [dispatch, navigate]);

	return (
		<Box id="app" className="App">
			<Helmet>
				<title>Affogato AI (formerly RenderNet AI): Create AI images with Unmatched Control</title>
				<script>
					{`
          var APP_ID = '${intercomAppId}';
          (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('update',w.intercomSettings);}
          else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};
          w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';
          s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}
          else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
          `}
				</script>
			</Helmet>
			<ThemeProvider theme={appTheme}>
				<Box sx={[reusableStyles.desktopUI]}>
					<ConditionalLayer isShown={isAppUpdateAvailable && !window.electron}>
						<FloatingAlert
							title="Affogato has an upgraded look."
							subtitle="Refresh your browser for a better experience."
							icon={<UpdateIcon />}
							dismissTime={604800000} // 7 days
							resetTimeInMinutes={5}
						/>
					</ConditionalLayer>
					<SignedOutModal />

					{openPricingModal?.open && <PricingModal />}
					<ExpandImageModal />
					<ExpandVideoModal />

					<MarketingPopUp />

					<DeletePromptConfirmationModal />

					<FeedPromptsModal />
					<ChangeAnythingFeedModal />
					<SubmitPromptForReviewPopUp />

					<HomePromptsModal open={openHomePromptsModal} />
					<Suspense fallback={<LoadingScreen />}>
						<AssetLibraryModal />
					</Suspense>

					<Suspense fallback={<LoadingScreen />}>
						<PoseControlTemplateModal />
					</Suspense>

					<Suspense fallback={<LoadingScreen />}>
						<DeletedUserModal />
					</Suspense>
					<Suspense fallback={<LoadingScreen />}>
						<SelectCanvasModal open={openSelectCanvasModal} />
					</Suspense>
					<Suspense fallback={<LoadingScreen />}>
						<FluxCharacterSelectModal />
					</Suspense>
					<Suspense fallback={<LoadingScreen />}>
						<VideoAnyoneModal />
					</Suspense>
					<LazyClothChangeModal />
					<LazyFaceReplaceModal />
					<LazyUpscale8kModal />
					<LazyProductPhotoModal />
					<LazyTrueTouchModal />
					<LazyHomeNarratorModal />
					<LazyMusicVideoCreateSceneModal />
					<LazyAudioCroppingModal />
					<LazyCreateNewCharacterModal />
					<Outlet />
				</Box>
			</ThemeProvider>
			<ToastContainer
				position="bottom-center"
				autoClose={3000}
				limit={1}
				hideProgressBar
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable={false}
				pauseOnHover
				theme="light"
				transition={Slide}
				icon={false}
				closeButton={false}
			/>
		</Box>
	);
}

posthog.init(process.env.REACT_APP_POSTHOG_ID, {
	api_host: 'https://app.posthog.com',
	autocapture: false,
	capture_pageview: false,
	session_recording: {
		maskAllInputs: false,
	},
	disable_session_recording: false,
});

amplitude.init(process.env.REACT_APP_AMPLITUDE_APP_KEY, null, {
	defaultTracking: false,
	includeReferrer: true,
	includeUtm: true,
});

datadogLogs.init({
	clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
	site: process.env.REACT_APP_DATADOG_SITE,
	service: process.env.REACT_APP_DATADOG_SERVICE,
	env: process.env.NODE_ENV,
	version: '1.11.0',
	sessionSampleRate: 100,
	forwardErrorsToLogs: true,
});

datadogRum.init({
	applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
	clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
	site: process.env.REACT_APP_DATADOG_SITE,
	service: process.env.REACT_APP_DATADOG_SERVICE,
	env: process.env.NODE_ENV,
	version: '1.11.0',
	sessionSampleRate: 100,
	sessionReplaySampleRate: 100,
	trackUserInteractions: true,
	trackResources: true,
	trackLongTasks: true,
	defaultPrivacyLevel: 'allow',
	forwardErrorsToLogs: true,
});

ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);

export default App;
